import React, { lazy, Suspense, useState } from 'react';
import { Button, Collapse, Space, Tag, Drawer, Flex } from 'antd';
// prettier-ignore
import { PlusOutlined, EyeOutlined, EditOutlined, HolderOutlined, PartitionOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslate, useDelete, useUpdate } from '@refinedev/core';
import { useModal } from '@refinedev/antd';

import apiEndpoint from 'core/common/apiEndPoints';
import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import RulePanel from 'pages/ruleChange/rule/RulePanel';
import RuleList from 'pages/ruleChange/rule/RuleList';
import RuleGroupModals from 'pages/ruleChange/ruleGroup/RuleGroupModals';
import usePermission from 'core/utils/usePermission';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import { getRenderRuleGroupDeleteButton } from 'pages/ruleChange/utils/getActionButton';
import Loading from 'components/loading/loading';

const RuleCreate = lazy(() => import('pages/ruleChange/rule/RuleCreate'));

// prettier-ignore
const RuleGroupAccordion = ({pageType,rulesDetails,refetch,appliedFilters,setAppliedFilters}) => {
  const t = useTranslate();
  const ruleListRefetch: Record<string, () => void> = {};
  const { mutate: deleteItem } = useDelete();
  const { mutate: reorderGroups } = useUpdate();
  const { rulePlanDataObj } = useRulesContext();
  const checkPermission = usePermission();
  const { userDetails } = useTokenContext();
  const groupModal = useModal();
  const createGroupDrawer = useModal();
  const reArrangeModal = useModal();

  const [activeGroup, setActiveGroup] = useState<{
    id: string;
    mode: string;
  } | null>(null);
  const [creatingRuleForGroup, setCreatingRuleForGroup] = useState<
    string | null
  >(null);
  const [activeCollapseKey, setActiveCollapseKey] = useState<
    string | undefined
  >(undefined);
  const [tempGroupList, setTempGroupList] = useState<any[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const filterList = rulesDetails?.filter_list;
  const groups = rulesDetails?.rule_group_list || [];

  const deleteEntity = (resource, id) => {
    deleteItem(
      { resource, id, successNotification: false, errorNotification: false },
      { onSuccess: () => refetch() }
    );
  };

  const handleDrop = (dropIndex: number) => {
    if (dragIndex === null || dragIndex === dropIndex) return;
    const updated = [...groups];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setDragIndex(null);
    setTempGroupList(updated);
    reArrangeModal.show();
  };

  const confirmReorder = () => {
    reorderGroups(
      {
        resource: apiEndpoint.ruleGroupDraft,
        values: tempGroupList,
        id: tempGroupList?.[0]?.unique_id,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          reArrangeModal.close();
          refetch();
        },
      }
    );
  };

  const cancelReorder = () => {
    setTempGroupList([]);
    reArrangeModal.close();
  };

  const activeGroupRecord = groups.find((g) => g.unique_id === activeGroup?.id);

  const renderRuleGroupDeleteButton = getRenderRuleGroupDeleteButton(
    deleteEntity,
    apiEndpoint,
    pageType,
    checkPermission,
    userDetails,
    t
  );

  return (
    <div className="">
      <RulePanel
        pageType={pageType}
        filterList={filterList}
        groups={groups}
        refetch={refetch}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        ruleListRefetch={ruleListRefetch}
      >
        {({ columns, filterButton }) => (
          <>
            <div style={{ padding: '16px 24px 8px' }}>
              <Flex justify="space-between" align="center" className="mb-sm">
                <h3 className=" text-base font-semibold">
                  <PartitionOutlined className="mr-sm" />
                  {t('ruleGroup.ruleGroup&rules')}
                </h3>
                <Space>
                  {filterButton}
                  {pageType !== 'Show' ? (
                    <Button
                      type="primary"
                      className="my-lg ml-xs secondary-btn"
                      icon={<PlusOutlined />}
                      onClick={() => createGroupDrawer.show()}
                    >
                      {t('rules.createGroup')}
                    </Button>
                  ) : null}
                </Space>
              </Flex>
            </div>
            <Collapse
              accordion
              bordered={false}
              expandIcon={() => null}
              className="rule-workspace-collapse"
              activeKey={activeCollapseKey}
              onChange={(key) =>
                setActiveCollapseKey(Array.isArray(key) ? key[0] : key)
              }
            >
              {groups.map((group, index) => (
                <Collapse.Panel
                  key={group.unique_id}
                  className="no-padding-panel"
                  header={
                    <Space
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDrop(index);
                      }}
                    >
                      <HolderOutlined
                        draggable
                        style={{ cursor: 'grab', color: '#bfbfbf' }}
                        onClick={(e) => e.stopPropagation()}
                        onDragStart={(e) => {
                          e.stopPropagation();
                          e.dataTransfer.setData('text/plain', String(index));
                          setDragIndex(index);
                        }}
                      />
                      {activeCollapseKey === group.unique_id ? (
                        <DownOutlined />
                      ) : (
                        <RightOutlined />
                      )}
                      <Space size={12} align="center">
                        <span className="font-semibold text-base">
                          {group.rule_group_name}
                        </span>
                        <Tag>
                          {group.rule_count || 0}{' '}
                          {group.rule_count === 1
                            ? t('ruleGroup.rules')
                            : t('ruleGroup.ruleCountPlural')}
                        </Tag>
                        <Tag
                          color={
                            group.rule_group_is_active ? 'green' : 'default'
                          }
                        >
                          {group.rule_group_is_active ? 'Active' : 'Inactive'}
                        </Tag>
                      </Space>
                    </Space>
                  }
                  extra={
                    <Space
                      onClick={(e) => e.stopPropagation()}
                      size={20}
                      className="mt-sm"
                    >
                      {pageType === 'Show' ? (
                        <EyeOutlined
                          className="a8-show-button"
                          onClick={() => {
                            setActiveGroup({
                              id: group.unique_id,
                              mode: 'Show',
                            });
                            groupModal.show();
                          }}
                        />
                      ) : null}

                      {pageType !== 'Show' ? (
                        <>
                          <EditOutlined
                            className="a8-show-button"
                            onClick={() => {
                              setActiveGroup({
                                id: group.unique_id,
                                mode: 'Edit',
                              });
                              groupModal.show();
                            }}
                          />
                          <PlusOutlined
                            className="a8-show-button"
                            onClick={() => {
                              setActiveCollapseKey(group.unique_id);
                              setCreatingRuleForGroup(group.unique_id);
                            }}
                          />
                          {renderRuleGroupDeleteButton(group)}
                        </>
                      ) : null}
                    </Space>
                  }
                >
                  {activeCollapseKey === group.unique_id ? (
                    <RuleList
                      key={`${group.unique_id}-${group.rule_count}-${group.rule_group_is_active}`}
                      groupId={group.unique_id}
                      appliedFilters={appliedFilters}
                      pageType={pageType}
                      isPublished={!!rulePlanDataObj?.view_unique_id}
                      onReady={(refetchFn) => {
                        ruleListRefetch[group.unique_id] = refetchFn;
                      }}
                      renderRules={({ rules, tableProps }) =>
                        columns.render(group, rules, tableProps)
                      }
                    />
                  ) : null}

                  {creatingRuleForGroup === group.unique_id ? (
                    <Drawer
                      title={t('rules.createRule')}
                      width="85%"
                      open
                      onClose={() => setCreatingRuleForGroup(null)}
                      destroyOnClose
                      maskClosable={false}
                    >
                      <Suspense fallback={<Loading />}>
                        <RuleCreate
                          type="Create"
                          groupId={group.unique_id}
                          pageType={pageType}
                          onDone={() => {
                            setCreatingRuleForGroup(null);
                            refetch();
                          }}
                        />
                      </Suspense>
                    </Drawer>
                  ) : null}
                </Collapse.Panel>
              ))}
            </Collapse>
          </>
        )}
      </RulePanel>
      <RuleGroupModals
        groupModal={groupModal}
        createGroupDrawer={createGroupDrawer}
        reArrangeModal={reArrangeModal}
        activeGroup={activeGroup}
        activeGroupRecord={activeGroupRecord}
        confirmReorder={confirmReorder}
        cancelReorder={cancelReorder}
        refetch={refetch}
        pageType={pageType}
        isPublished={!!rulePlanDataObj?.view_unique_id}
        ruleListRefetch={ruleListRefetch}
      />
    </div>
  );
};

export default RuleGroupAccordion;
